import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ComplianceDisclaimer } from '../components/ComplianceDisclaimer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Calendar,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Calculator,
  FileText,
  Database,
  Send,
  Clock,
  AlertCircle,
  PoundSterling,
  ArrowRight,
} from 'lucide-react';

function getThresholdResult(income) {
  if (income > 50000) {
    return {
      label: 'You must join MTD from 6 April 2026',
      detail: 'Your combined gross income exceeds £50,000. You are in the first mandatory cohort.',
      color: 'bg-red-50 border-red-300 text-red-800',
      icon: 'red',
    };
  }
  if (income > 30000) {
    return {
      label: 'You must join MTD from 6 April 2027',
      detail: 'Your combined gross income is between £30,001 and £50,000.',
      color: 'bg-amber-50 border-amber-300 text-amber-800',
      icon: 'amber',
    };
  }
  if (income > 20000) {
    return {
      label: 'You must join MTD from 6 April 2028',
      detail: 'Your combined gross income is between £20,001 and £30,000. This threshold is confirmed in policy; legislation is pending.',
      color: 'bg-amber-50 border-amber-300 text-amber-800',
      icon: 'amber',
    };
  }
  return {
    label: 'Not currently in scope',
    detail: 'Your combined gross income is £20,000 or below. You are not required to join MTD for Income Tax under current policy.',
    color: 'bg-green-50 border-green-300 text-green-800',
    icon: 'green',
  };
}

const timelineEvents = [
  {
    date: '6 Apr 2026',
    label: 'MTD mandatory for landlords earning over £50,000',
    detail: 'Combined gross income from property and self-employment, assessed on 2024–25 tax return.',
    accent: 'bg-red-500',
  },
  {
    date: '7 Aug 2026',
    label: 'First quarterly update deadline',
    detail: 'Q1: 6 April – 5 July. Subsequent deadlines: 7 Nov, 7 Feb, 7 May each year.',
    accent: 'bg-navy-600',
  },
  {
    date: '6 Apr 2027',
    label: 'Threshold drops to over £30,000',
    detail: 'Landlords with combined income over £30,000 must join. Assessed on 2025–26 tax return.',
    accent: 'bg-amber-500',
  },
  {
    date: '6 Apr 2028',
    label: 'Threshold drops to over £20,000',
    detail: 'Confirmed in government policy; secondary legislation pending. Assessed on 2026–27 tax return.',
    accent: 'bg-amber-400',
  },
];

const quarterlyDeadlines = [
  { period: '6 Apr – 5 Jul', deadline: '7 August' },
  { period: '6 Jul – 5 Oct', deadline: '7 November' },
  { period: '6 Oct – 5 Jan', deadline: '7 February' },
  { period: '6 Jan – 5 Apr', deadline: '7 May' },
];

const obligationCards = [
  {
    icon: Database,
    title: 'Keep digital records',
    body: 'Record all property income and expenses digitally throughout the year using HMRC-compatible software, or a spreadsheet with bridging software.',
    color: 'bg-royal-100 text-royal-600',
  },
  {
    icon: Send,
    title: 'Submit quarterly summaries',
    body: 'Send a simple income and expense summary to HMRC four times a year. This is not a full tax return — just a check-and-send of your running totals.',
    color: 'bg-amber-100 text-amber-700',
  },
  {
    icon: FileText,
    title: 'End-of-year declaration',
    body: 'After the tax year ends, submit an End-of-Period Statement for each income source, then a Final Declaration that pulls in all your personal income — replacing the current Self Assessment return.',
    color: 'bg-green-100 text-green-700',
  },
];

const usefulLinks = [
  {
    title: 'MTD for Income Tax step-by-step guide',
    description: 'Official GOV.UK collection: everything you need to know to get started.',
    url: 'https://www.gov.uk/government/collections/making-tax-digital-for-income-tax-for-businesses-step-by-step',
  },
  {
    title: 'Sign up for Making Tax Digital',
    description: "Register your business for MTD for Income Tax on HMRC's sign-up service.",
    url: 'https://www.gov.uk/guidance/sign-up-your-business-for-making-tax-digital-for-income-tax',
  },
  {
    title: 'Find compatible software',
    description: 'Browse HMRC-approved software that works with MTD for Income Tax.',
    url: 'https://www.gov.uk/guidance/find-software-thats-compatible-with-making-tax-digital-for-income-tax',
  },
];

export const MtdGuide = () => {
  useEffect(() => { document.title = 'MTD for Landlords Guide - SIYAK'; }, []);
  const [incomeInput, setIncomeInput] = useState('');
  const parsedIncome = parseFloat(incomeInput.replace(/,/g, '')) || 0;
  const result = incomeInput.trim() !== '' ? getThresholdResult(parsedIncome) : null;

  return (
    <Layout>
      {/* Hero */}
      <section className="relative bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy-900 to-navy-800" />
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-amber-400 uppercase tracking-wide mb-3">
              Making Tax Digital · 6 April 2026
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Making Tax Digital for Landlords
            </h1>
            <p className="text-lg text-navy-200 mb-8 leading-relaxed">
              From 6 April 2026, many UK landlords must keep digital records and submit
              quarterly income summaries to HMRC. Find out if you're affected, when your
              deadline is, and what you need to do.
            </p>
            <Button
              size="lg"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => {
                const el = document.getElementById('threshold-checker');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}
            >
              <Calculator className="mr-2 h-5 w-5" />
              Check if you're affected
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 space-y-10">

        {/* Threshold Checker */}
        <section id="threshold-checker">
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-amber-100">
                  <Calculator className="h-5 w-5 text-amber-600" />
                </div>
                <CardTitle className="text-xl font-bold text-navy-900">
                  When do you need to join MTD?
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter your combined gross annual income from property and self-employment to find out your mandatory start date.
              </p>

              <div className="flex items-center gap-2 max-w-xs">
                <span className="flex items-center justify-center w-9 h-9 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground font-medium text-sm flex-shrink-0">
                  <PoundSterling className="h-4 w-4" />
                </span>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 45000"
                  value={incomeInput}
                  onChange={(e) => setIncomeInput(e.target.value.replace(/[^0-9,.]/g, ''))}
                  className="rounded-l-none"
                />
              </div>

              {result && (
                <div className={`flex items-start gap-3 border rounded-lg px-4 py-3 ${result.color}`}>
                  {result.icon === 'green' ? (
                    <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5 text-amber-600" />
                  )}
                  <div>
                    <p className="font-semibold text-sm">{result.label}</p>
                    <p className="text-xs mt-0.5 opacity-90">{result.detail}</p>
                  </div>
                </div>
              )}

              <div className="space-y-1 pt-1">
                <p className="text-xs text-muted-foreground">
                  This is your combined gross income from property and self-employment, per individual — not per property.
                </p>
                <p className="text-xs text-muted-foreground">
                  Limited company landlords are exempt from MTD for Income Tax.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Key Dates Timeline */}
        <section>
          <h2 className="font-heading text-2xl font-bold text-navy-900 mb-6 flex items-center gap-2">
            <Calendar className="h-6 w-6 text-royal-600" />
            Key dates
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {timelineEvents.map((event, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex gap-4"
              >
                <div className={`w-1 rounded-full flex-shrink-0 ${event.accent}`} />
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                    {event.date}
                  </p>
                  <p className="font-semibold text-navy-900 text-sm mb-1">{event.label}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{event.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <Card className="border-0 shadow-sm bg-navy-50">
            <CardHeader className="pb-4">
              <CardTitle className="text-base font-semibold text-navy-900 flex items-center gap-2">
                <Clock className="h-4 w-4 text-royal-600" />
                Quarterly submission pattern (each year)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {quarterlyDeadlines.map((q, i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-100 p-4 text-center">
                    <p className="text-xs text-muted-foreground mb-1">{q.period}</p>
                    <p className="font-semibold text-navy-900 text-sm">Due {q.deadline}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                Plus a Final Declaration by 31 January following the end of the tax year.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* What you need to do */}
        <section>
          <h2 className="font-heading text-2xl font-bold text-navy-900 mb-6 flex items-center gap-2">
            <CheckCircle className="h-6 w-6 text-green-600" />
            What you need to do
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {obligationCards.map((card, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 ${card.color}`}>
                  <card.icon className="h-6 w-6" />
                </div>
                <h3 className="font-heading font-semibold text-navy-900 mb-2">{card.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Penalties */}
        <section>
          <h2 className="font-heading text-2xl font-bold text-navy-900 mb-4 flex items-center gap-2">
            <AlertCircle className="h-6 w-6 text-red-500" />
            Penalties
          </h2>

          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6 space-y-4">
              <div className="flex gap-4 items-start">
                <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-2" />
                <div>
                  <p className="font-medium text-navy-900 text-sm">Points-based system</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Each missed quarterly submission deadline earns 1 penalty point. At 4 points, a
                    £200 fine is triggered. Every subsequent late submission adds a further £200.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0 mt-2" />
                <div>
                  <p className="font-medium text-navy-900 text-sm">
                    Soft landing 2026–27 <span className="text-xs font-normal text-green-700 bg-green-50 px-2 py-0.5 rounded-full ml-1">First cohort only</span>
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    HMRC has confirmed no penalty points will be applied for late quarterly updates
                    during the 2026–27 tax year for those mandated from April 2026. This soft
                    landing applies to quarterly updates only — the Final Declaration deadline is
                    not covered.
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-2 h-2 rounded-full bg-navy-600 flex-shrink-0 mt-2" />
                <div>
                  <p className="font-medium text-navy-900 text-sm">Late payment penalties</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Separate late payment penalties apply from 16 days after the payment due date. These are not affected by the soft landing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Useful Links */}
        <section>
          <h2 className="font-heading text-2xl font-bold text-navy-900 mb-4 flex items-center gap-2">
            <ExternalLink className="h-6 w-6 text-royal-600" />
            Useful links
          </h2>

          <div className="space-y-4">
            {usefulLinks.map((link, i) => (
              <a
                key={i}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between gap-4 bg-white rounded-2xl shadow-sm border border-gray-100 p-5 hover:shadow-md hover:border-royal-200 transition-all group"
              >
                <div>
                  <p className="font-semibold text-navy-900 text-sm group-hover:text-royal-600 transition-colors">
                    {link.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">{link.description}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-royal-600 flex-shrink-0 transition-colors" />
              </a>
            ))}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="rounded-2xl bg-navy-900 p-8 text-center">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-3">
            Track your rental income and expenses with SIYAK
          </h2>
          <p className="text-navy-200 mb-6 max-w-lg mx-auto text-sm">
            SIYAK helps landlords stay organised — track income, log expenses, and keep the
            digital records you'll need for MTD.
          </p>
          <Button size="lg" className="bg-royal-600 hover:bg-royal-700" asChild>
            <Link to="/register">
              Get started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </section>

        <ComplianceDisclaimer />
      </div>
    </Layout>
  );
};
